import { useAppSelector } from '@/store/hooks';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { selectReportingV2Filters } from '@/store/slices/reportingV2/reportingV2Slice';
import {
  EXPORT_SECTIONS,
  buildStackedCsv,
  downloadFile,
  presentRowsForExport,
  presentTotalRowForExport,
} from '../lib/reportExport';
import { useReportingPermissions } from './useReportingPermissions';

const SECTIONS_BY_KEY = new Map(EXPORT_SECTIONS.map(s => [s.key, s]));

/**
 * Per-widget "export just this chart/table" (flank#4457 — matches legacy
 * Reporting's per-component export; the page-level ExportDialog already lets
 * someone pick a single section, but that's a detour through a dialog for
 * what should be one click on the widget itself).
 *
 * Reuses the exact same builders as the page export so a lone widget's file
 * agrees byte-for-byte with what that section would produce inside the full
 * export — same column rules, same CSV-injection neutralization.
 *
 * `sectionKey` takes a plain string (not the narrower EXPORT_SECTIONS union)
 * so components like BreakdownCard, whose own `widgetKey` prop is generic,
 * can call this without a cast; a key with no matching section is a no-op.
 *
 * `totalRows` is only meaningful for a server-paged widget (Impressions by
 * Type) — omit it for anything else. See `truncationNote` (flank#4567): a
 * paged widget's `rows` is just the current page, and without this the export
 * would silently produce a truncated file with nothing saying so.
 *
 * `extraRow` appends one more row after the real ones — currently just
 * Impressions by Type's Total row (flank#4645), already computed by the
 * caller (it knows which column is the active chip's name column; this hook
 * doesn't). Applied after `presentRowsForExport` so the already-final Total
 * values don't go through dimension-labelling/margin a second time — still
 * goes through `presentTotalRowForExport` here, the Total row's own
 * finalizing step (money rounding + withholding client-money fields the
 * grid itself would hide), since building `extraRow` doesn't apply either.
 *
 * `clientSpendRestricted` — same gate `clientMoneyNoteForExport` applies to
 * the page-level export (review, flank#4655); the caller already has its own
 * `restrictedFields` in scope from `selectReportingV2Widget`, so it's passed
 * through rather than re-selected here.
 *
 * `totalRowMismatch` — same gate `totalRowMismatchNoteForExport` applies to
 * the page-level export (review, Lucas, flank#4683): Impressions by Type's
 * caller already knows whether the active chip is one `isTotalRowMismatched`
 * flags, so it's passed through rather than re-derived here.
 */
export const useExportWidgetCsv = (
  sectionKey: string,
  rows: ReportRow[],
  totalRows?: number,
  extraRow?: ReportRow,
  clientSpendRestricted?: boolean,
  totalRowMismatch?: boolean,
  liveReach?: boolean,
) => {
  const section = SECTIONS_BY_KEY.get(sectionKey);
  const filters = useAppSelector(selectReportingV2Filters);
  // flank#4625 sweep — the per-widget export button reuses the same builder
  // as the page-level dialog, so it needs the same permission filtering.
  const permissions = useReportingPermissions();

  return () => {
    if (!section || rows.length === 0) return;
    const meta = [
      section.label,
      `Date range: ${filters.dateStart} to ${filters.dateEnd}`,
      `Exported: ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`,
    ];
    const exportRows = presentRowsForExport(rows, section.key, filters.dsps, permissions, liveReach);
    const safeExtraRow = extraRow ? presentTotalRowForExport(extraRow, rows) : undefined;
    const csv = buildStackedCsv(
      [
        {
          ...section,
          rows: safeExtraRow ? [...exportRows, safeExtraRow] : exportRows,
          totalRows,
          clientSpendRestricted,
          totalRowMismatch,
        },
      ],
      meta,
    );
    const stamp = `${filters.dateStart}_to_${filters.dateEnd}`;
    downloadFile(csv, `${section.key}_${stamp}.csv`, 'text/csv');
  };
};
